export const queryKeys = {
  // Dashboard
  dashboard: {
    all: ["dashboard"] as const,
    kpis: () => [...queryKeys.dashboard.all, "kpis"] as const,
    activity: () => [...queryKeys.dashboard.all, "activity"] as const,
    revenue: () => [...queryKeys.dashboard.all, "revenue"] as const,
    funnel: () => [...queryKeys.dashboard.all, "funnel"] as const,
    health: () => [...queryKeys.dashboard.all, "health"] as const,
    freshness: () => [...queryKeys.dashboard.all, "freshness"] as const,
    userGrowth: () => [...queryKeys.dashboard.all, "user-growth"] as const,
    socialPipeline: () => [...queryKeys.dashboard.all, "social-pipeline"] as const,
  },

  // Properties
  properties: {
    all: ["properties"] as const,
    list: (params: Record<string, unknown>) =>
      [...queryKeys.properties.all, "list", params] as const,
    detail: (id: string) => [...queryKeys.properties.all, "detail", id] as const,
    qualityReview: (params: Record<string, unknown>) =>
      [...queryKeys.properties.all, "quality-review", params] as const,
  },

  // Clients
  clients: {
    all: ["clients"] as const,
    list: (params: Record<string, unknown>) => [...queryKeys.clients.all, "list", params] as const,
    detail: (id: string) => [...queryKeys.clients.all, "detail", id] as const,
    pipeline: (id: string) => [...queryKeys.clients.all, "pipeline", id] as const,
    interactions: (id: string) => [...queryKeys.clients.all, "interactions", id] as const,
  },

  // Agents
  agents: {
    all: ["agents"] as const,
    list: (params: Record<string, unknown>) => [...queryKeys.agents.all, "list", params] as const,
    detail: (id: string) => [...queryKeys.agents.all, "detail", id] as const,
    performance: (id: string) => [...queryKeys.agents.all, "performance", id] as const,
    leaderboard: () => [...queryKeys.agents.all, "leaderboard"] as const,
  },

  // Listing Agents (selling agents from scraped listings)
  listingAgents: {
    all: ["listing-agents"] as const,
    list: (params: Record<string, unknown>) =>
      [...queryKeys.listingAgents.all, "list", params] as const,
    detail: (id: string) =>
      [...queryKeys.listingAgents.all, "detail", id] as const,
    listings: (id: string, params: Record<string, unknown>) =>
      [...queryKeys.listingAgents.all, "listings", id, params] as const,
    sources: () => [...queryKeys.listingAgents.all, "sources"] as const,
    languages: () => [...queryKeys.listingAgents.all, "languages"] as const,
  },

  // Content
  content: {
    all: ["content"] as const,
    list: (params: Record<string, unknown>) => [...queryKeys.content.all, "list", params] as const,
    detail: (id: string) => [...queryKeys.content.all, "detail", id] as const,
  },

  // Newsletter
  newsletter: {
    all: ["newsletter"] as const,
    list: (params: Record<string, unknown>) =>
      [...queryKeys.newsletter.all, "list", params] as const,
    detail: (id: string) => [...queryKeys.newsletter.all, "detail", id] as const,
    analytics: (id: string) => [...queryKeys.newsletter.all, "analytics", id] as const,
    recipients: (id: string, params: Record<string, unknown>) =>
      [...queryKeys.newsletter.all, "recipients", id, params] as const,
  },

  // Data Quality
  dataQuality: {
    all: ["data-quality"] as const,
    sources: () => [...queryKeys.dataQuality.all, "sources"] as const,
    ingestion: (params: Record<string, unknown>) =>
      [...queryKeys.dataQuality.all, "ingestion", params] as const,
    anomalies: (params: Record<string, unknown>) =>
      [...queryKeys.dataQuality.all, "anomalies", params] as const,
    freshness: () => [...queryKeys.dataQuality.all, "freshness"] as const,
    dedup: () => [...queryKeys.dataQuality.all, "dedup"] as const,
  },

  // AI Models
  models: {
    all: ["models"] as const,
    registry: () => [...queryKeys.models.all, "registry"] as const,
    detail: (id: string) => [...queryKeys.models.all, "detail", id] as const,
    accuracy: (id: string) => [...queryKeys.models.all, "accuracy", id] as const,
    drift: (id: string) => [...queryKeys.models.all, "drift", id] as const,
    backtest: (id: string) => [...queryKeys.models.all, "backtest", id] as const,
  },

  // Billing
  billing: {
    all: ["billing"] as const,
    subscribers: (params: Record<string, unknown>) =>
      [...queryKeys.billing.all, "subscribers", params] as const,
    revenue: () => [...queryKeys.billing.all, "revenue"] as const,
    churn: () => [...queryKeys.billing.all, "churn"] as const,
    trials: () => [...queryKeys.billing.all, "trials"] as const,
    referrals: () => [...queryKeys.billing.all, "referrals"] as const,
    tierBreakdown: () => [...queryKeys.billing.all, "tier-breakdown"] as const,
  },

  // System
  system: {
    all: ["system"] as const,
    config: () => [...queryKeys.system.all, "config"] as const,
    featureFlags: () => [...queryKeys.system.all, "feature-flags"] as const,
    health: () => [...queryKeys.system.all, "health"] as const,
    environment: () => [...queryKeys.system.all, "environment"] as const,
  },

  // Platform Users
  platformUsers: {
    all: ["platform-users"] as const,
    list: (params: Record<string, unknown>) =>
      [...queryKeys.platformUsers.all, "list", params] as const,
    detail: (id: string) => [...queryKeys.platformUsers.all, "detail", id] as const,
  },

  // Admin Users
  users: {
    all: ["users"] as const,
    list: (params: Record<string, unknown>) => [...queryKeys.users.all, "list", params] as const,
    detail: (id: string) => [...queryKeys.users.all, "detail", id] as const,
  },

  // Alerts
  alerts: {
    all: ["alerts"] as const,
    list: (params: Record<string, unknown>) => [...queryKeys.alerts.all, "list", params] as const,
    unacknowledgedCount: () => [...queryKeys.alerts.all, "unacknowledged-count"] as const,
  },

  // Audit
  audit: {
    all: ["audit"] as const,
    list: (params: Record<string, unknown>) => [...queryKeys.audit.all, "list", params] as const,
    detail: (id: string) => [...queryKeys.audit.all, "detail", id] as const,
  },

  // Leads
  leads: {
    all: ["leads"] as const,
    list: (params: Record<string, unknown>) => [...queryKeys.leads.all, "list", params] as const,
    detail: (id: string) => [...queryKeys.leads.all, "detail", id] as const,
    signals: (id: string) => [...queryKeys.leads.all, "signals", id] as const,
    scores: (id: string) => [...queryKeys.leads.all, "scores", id] as const,
    interactions: (id: string) => [...queryKeys.leads.all, "interactions", id] as const,
    consents: (id: string) => [...queryKeys.leads.all, "consents", id] as const,
    pipelineStages: () => [...queryKeys.leads.all, "pipeline-stages"] as const,
    pipelineSummary: () => [...queryKeys.leads.all, "pipeline-summary"] as const,
    dashboard: () => [...queryKeys.leads.all, "dashboard"] as const,
    outreach: () => [...queryKeys.leads.all, "outreach"] as const,
    partners: (params: Record<string, unknown>) =>
      [...queryKeys.leads.all, "partners", params] as const,
    partnerDetail: (id: string) => [...queryKeys.leads.all, "partner-detail", id] as const,
    partnerCommissions: (id: string) =>
      [...queryKeys.leads.all, "partner-commissions", id] as const,
    compliance: () => [...queryKeys.leads.all, "compliance"] as const,
    reports: (params: Record<string, unknown>) =>
      [...queryKeys.leads.all, "reports", params] as const,
    visits: (params: Record<string, unknown>) =>
      [...queryKeys.leads.all, "visits", params] as const,
    visitDetail: (id: string) => [...queryKeys.leads.all, "visit-detail", id] as const,
    scoringDashboard: () => [...queryKeys.leads.all, "scoring-dashboard"] as const,
  },

  // Vehicles
  vehicles: {
    all: ["vehicles"] as const,
    health: () => [...queryKeys.vehicles.all, "health"] as const,
    list: (params: Record<string, unknown>) =>
      [...queryKeys.vehicles.all, "list", params] as const,
    stats: () => [...queryKeys.vehicles.all, "stats"] as const,
  },

  // Platform Logs
  logs: {
    all: ["logs"] as const,
    services: () => [...queryKeys.logs.all, "services"] as const,
    service: (name: string, lines: number) =>
      [...queryKeys.logs.all, "service", name, lines] as const,
  },
} as const;
